import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { PaginationMetadata, ReportListKey, ReportState } from "../../types";
import { axiosObj } from "../../../config/apiClients";
import { queryParams } from "../../../utils/interface";
import { buildUrlWithParams } from "../../../utils/helpers/common";
import { UpdateCashbackPayload, UpdateEquity } from "../../../utils/types";

const initialPagination: PaginationMetadata = {
  totalItems: 0,
  currentPage: 1,
  totalPages: 1,
  hasNextPage: false
}

const REPORT_KEYS: ReportListKey[] = [
  "moneyTransactions", "esouqOrders", "statements", "demoUsersStatements",
  "dailyUsersStatement", "usersCashback", "investmentInsights",
  "balanceMismatched", "loans",
];

const buildInitial = <T>(value: () => T) =>
  Object.fromEntries(REPORT_KEYS.map((k) => [k, value()])) as Record<ReportListKey, T>;

/** Pull the standard pagination block out of a report response payload. */
const readPagination = (payload: any): PaginationMetadata => ({
  totalItems: payload?.totalItems ?? 0,
  currentPage: payload?.page ?? 1,
  totalPages: payload?.totalPages ?? 1,
  hasNextPage: payload?.hasNextPage ?? false,
});

const initialState: ReportState = {
  listLoading: buildInitial(() => false),
  listPagination: buildInitial(() => ({ ...initialPagination })),
  totalEquity: 0,
  dailyStatementHoldings: null,
  isUpdatingCashback: false,
  isUpdatingEquity: false,
  isPushingCashback: false,
  isLoadingCashbackDetail: false,
  isLoadingAutoCashback: false,
  isLoadingCashbackClients: false,
  isMonthlyCashbackEnabled: false,
  moneyTransactionsReport: [],
  loansReport: [],
  esouqOrdersReport: [],
  statementsReport: [],
  demoUsersStatementsReport: [],
  dailyUsersStatementReport: [],
  usersCashbackReport: [],
  userCashbackReport: [],
  investmentInsightsReport: [],
  totalCashbackPerMonth: 0,
  totalUserHoldings: null,
  transactionTypeSummary: [],
  esouqOrdersSummary: [],
  cashbackEligibleUsers: [],
  cashbackClients: [],
  balanceMismatchedUsers: [],
  eligibleUsersPagination: initialPagination,
  cashbackClientsPagination: initialPagination
};

export const getMoneyTransactionsReport = createAsyncThunk(
  "/reports/moneytransactions",
  async (query: queryParams, { rejectWithValue }) => {
    try {
      const url = buildUrlWithParams('reports/moneytransactions', query);
      const response = await axiosObj.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Money Transactions Report' });
    }
  }
);

// export const getLoansReport = createAsyncThunk(
//   "/reports/loans",
//   async (query: SearchQuery, { rejectWithValue }) => {
//     try {
//       const url = buildUrlWithParams('reports/loans', query);
//       const response = await axiosObj.get(url);
//       return response.data;
//     } catch (error) {
//       return rejectWithValue(error || { message: 'Failed to Fetch Loans Report' });
//     }
//   }
// );

export const getEsouqOrdersReport = createAsyncThunk(
  "/reports/esouq-orders",
  async (query: queryParams, { rejectWithValue }) => {
    try {
      const url = buildUrlWithParams(`reports/esouqOrders`, query);
      const response = await axiosObj.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Esouq Orders Report' });
    }
  }
);

export const getStatementsReport = createAsyncThunk(
  "/reports/statements",
  async (query: queryParams, { rejectWithValue }) => {
    try {
      const url = buildUrlWithParams('reports/statements', query);
      const response = await axiosObj.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Statements Report' });
    }
  }
);

export const getDemoUsersStatementsReport = createAsyncThunk(
  "/reports/demoUsersStatements",
  async (query: queryParams, { rejectWithValue }) => {
    try {
      const url = buildUrlWithParams('reports/demoUsersStatements', query);
      const response = await axiosObj.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Demo Users Statements Report' });
    }
  }
);

export const getDailyUsersStatementReport = createAsyncThunk(
  "/reports/dailyUsersStatements",
  async (query: queryParams, { rejectWithValue }) => {
    try {
      const url = buildUrlWithParams('reports/getAllUsersStatement', query);
      const response = await axiosObj.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Demo Users Statements Report' });
    }
  }
);

export const updateEquity = createAsyncThunk(
  "/reports/dailyUsersStatements/updateEquity",
  async (data: UpdateEquity, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('reports/usersStatement/updateEquity', data);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Update Equity' });
    }
  }
);

export const getUsersCashbackReport = createAsyncThunk(
  "/reports/usersCashbackReport",
  async (query: queryParams, { rejectWithValue }) => {
    try {
      const url = buildUrlWithParams('cashback/getAll', query);
      const response = await axiosObj.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch User Cashback Report' });
    }
  }
);

export const getUserDetailedCashbackReport = createAsyncThunk(
  "/reports/usersCashbackReport/details",
  async (query: { accountId: string, month?: number, year?: number }, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get(`cashback/details?accountId=${query.accountId}${query.month && query.year ? `&month=${query.month}&year=${query.year}` : ''}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch User Detailed Cashback Report' });
    }
  }
);

export const updateCashback = createAsyncThunk(
  "/reports/cashback/update",
  async (cashback: UpdateCashbackPayload, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('cashback/update', cashback);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Update Cashback' });
    }
  }
);

export const getIsAutoCashbackEnabled = createAsyncThunk(
  "/reports/cashback/getIsAutoCashbackEnabled",
  async (_, { rejectWithValue }) => {
    try {
      const response = await axiosObj.get('/settings/getAll');
      const { isMonthlyCashbackEnabled } = response.data.payload.settings[0];
      return isMonthlyCashbackEnabled;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Get Auto Cashback' });
    }
  }
)

export const updateAutoCashbackEnabled = createAsyncThunk(
  "/reports/cashback/updateAutoCashbackEnabled",
  async (isMonthlyCashbackEnabled: boolean, { rejectWithValue }) => {
    try {
      const response = await axiosObj.patch('/cashback/auto', { isMonthlyCashbackEnabled });
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Update Enabled/Disabled AutoCashback' });
    }
  }
)

export const getInvestmentInsightsReport = createAsyncThunk(
  "/reports/investmentInsights",
  async (query: queryParams, { rejectWithValue }) => {
    try {
      const url = buildUrlWithParams('reports/investment-insights', query);
      const response = await axiosObj.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Investment Insights Report' });
    }
  }
);

export const pushCashback = createAsyncThunk(
  'reports/pushCashback',
  async (payload: { month: number; year: number }, { rejectWithValue }) => {
    try {
      const response = await axiosObj.post('/cashback/addManually', payload)
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Post Cashback' });
    }
  }
);


export const getEligibleCashbackUser = createAsyncThunk(
  "/reports/cashback/investmentInsights",
  async (query: queryParams, { rejectWithValue }) => {
    try {
      const url = buildUrlWithParams('cashback/eligible-users', query);
      const response = await axiosObj.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Eligible Cashback users.' });
    }
  }
);

/**
 * Clients that have cashback statements inside a date range, with the percentage
 * currently applied to them. Backs the per-client overrides in the Update Cashback modal.
 */
export const getCashbackClients = createAsyncThunk(
  "/reports/cashback/clients",
  async (query: queryParams, { rejectWithValue }) => {
    try {
      const url = buildUrlWithParams('cashback/eligible-users', query);
      const response = await axiosObj.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Cashback Clients.' });
    }
  }
);

export const getBalanceMismatchedUsers = createAsyncThunk(
  "reports/getBalanceMismatchedUsers",
  async (params: { search?: string; paramPage: number; paramLimit: number }, { rejectWithValue }) => {
    try {
      const url = buildUrlWithParams('reports/balance-mismatch', params);
      const response = await axiosObj.get(url);
      return response.data;
    } catch (error) {
      return rejectWithValue(error || { message: 'Failed to Fetch Balance Mismatched users.' });
    }
  }
);

const reportsSlice = createSlice({
  name: "Reports",
  initialState,
  reducers: {
    toggleAutoCashback: (state, action) => {
      state.isMonthlyCashbackEnabled = action.payload;
    }
  },
  extraReducers(builder) {
    builder
      .addCase(getMoneyTransactionsReport.pending, (state) => {
        state.listLoading.moneyTransactions = true;
      })
      .addCase(getMoneyTransactionsReport.fulfilled, (state, action) => {
        state.listLoading.moneyTransactions = false;
        state.moneyTransactionsReport = action.payload.payload.moneytransations;
        state.transactionTypeSummary = action.payload.payload.transactionTypeSummary;
        state.listPagination.moneyTransactions = readPagination(action.payload.payload);
      })
      .addCase(getMoneyTransactionsReport.rejected, (state) => {
        state.listLoading.moneyTransactions = false;
        state.moneyTransactionsReport = [];
        state.transactionTypeSummary = [];
      })
      // .addCase(getLoansReport.pending, (state) => {
      //   state.isLoading = true;
      // })
      // .addCase(getLoansReport.fulfilled, (state, action) => {
      //   state.isLoading = false;
      //   state.loansReport = action.payload.payload;
      // })
      // .addCase(getLoansReport.rejected, (state) => {
      //   state.isLoading = false;
      //   state.loansReport = [];
      // })
      .addCase(getEsouqOrdersReport.pending, (state) => {
        state.listLoading.esouqOrders = true;
      })
      .addCase(getEsouqOrdersReport.fulfilled, (state, action) => {
        state.listLoading.esouqOrders = false;
        state.esouqOrdersReport = action.payload.payload.esouqOrdersReport;
        state.esouqOrdersSummary = action.payload.payload.summary;
        state.listPagination.esouqOrders = readPagination(action.payload.payload);
      })
      .addCase(getEsouqOrdersReport.rejected, (state) => {
        state.listLoading.esouqOrders = false;
        state.esouqOrdersReport = [];
      })
      .addCase(getStatementsReport.pending, (state) => {
        state.listLoading.statements = true;
      })
      .addCase(getStatementsReport.fulfilled, (state, action) => {
        state.listLoading.statements = false;
        state.statementsReport = action.payload.payload.statementsReport;
        state.listPagination.statements = readPagination(action.payload.payload);
      })
      .addCase(getStatementsReport.rejected, (state) => {
        state.listLoading.statements = false;
        state.statementsReport = [];
      })
      .addCase(getDemoUsersStatementsReport.pending, (state) => {
        state.listLoading.demoUsersStatements = true;
      })
      .addCase(getDemoUsersStatementsReport.fulfilled, (state, action) => {
        state.listLoading.demoUsersStatements = false;
        state.demoUsersStatementsReport = action.payload.payload.statementsReport;
        state.listPagination.demoUsersStatements = readPagination(action.payload.payload);
      })
      .addCase(getDemoUsersStatementsReport.rejected, (state) => {
        state.listLoading.demoUsersStatements = false;
        state.demoUsersStatementsReport = [];
      })
      .addCase(getDailyUsersStatementReport.pending, (state) => {
        state.listLoading.dailyUsersStatement = true;
      })
      .addCase(getDailyUsersStatementReport.fulfilled, (state, action) => {
        state.listLoading.dailyUsersStatement = false;
        state.dailyUsersStatementReport = action.payload.payload.statements;
        state.totalEquity = action.payload.payload.totalEquity;
        state.dailyStatementHoldings = action.payload.payload?.totalUserHoldings ?? null;
        state.listPagination.dailyUsersStatement = readPagination(action.payload.payload);
      })
      .addCase(getDailyUsersStatementReport.rejected, (state) => {
        state.listLoading.dailyUsersStatement = false;
        state.dailyUsersStatementReport = [];
      })
      .addCase(getUsersCashbackReport.pending, (state) => {
        state.listLoading.usersCashback = true;
      })
      .addCase(getUsersCashbackReport.fulfilled, (state, action) => {
        state.listLoading.usersCashback = false;
        state.usersCashbackReport = action.payload.payload.allUsersCashback;
        state.totalCashbackPerMonth = action.payload.payload.totalCashbackPerMonth;
        state.listPagination.usersCashback = readPagination(action.payload.payload);
      })
      .addCase(getUsersCashbackReport.rejected, (state) => {
        state.listLoading.usersCashback = false;
        state.usersCashbackReport = [];
      })
      .addCase(updateEquity.pending, (state) => {
        state.isUpdatingEquity = true;
      })
      .addCase(updateEquity.fulfilled, (state) => {
        state.isUpdatingEquity = false;
      })
      .addCase(updateEquity.rejected, (state) => {
        state.isUpdatingEquity = false;
      })
      .addCase(updateCashback.pending, (state) => {
        state.isUpdatingCashback = true;
      })
      .addCase(updateCashback.fulfilled, (state) => {
        state.isUpdatingCashback = false;
      })
      .addCase(updateCashback.rejected, (state) => {
        state.isUpdatingCashback = false;
      })
      .addCase(getIsAutoCashbackEnabled.pending, (state) => {
        state.isLoadingAutoCashback = true;
      })
      .addCase(getIsAutoCashbackEnabled.fulfilled, (state, action) => {
        state.isLoadingAutoCashback = false;
        state.isMonthlyCashbackEnabled = action.payload;
      })
      .addCase(getIsAutoCashbackEnabled.rejected, (state) => {
        state.isLoadingAutoCashback = false;
      })
      .addCase(getUserDetailedCashbackReport.pending, (state) => {
        state.isLoadingCashbackDetail = true;
      })
      .addCase(getUserDetailedCashbackReport.fulfilled, (state, action) => {
        state.isLoadingCashbackDetail = false;
        state.userCashbackReport = action.payload.payload.userDetailedReport;
      })
      .addCase(getUserDetailedCashbackReport.rejected, (state) => {
        state.isLoadingCashbackDetail = false;
        state.userCashbackReport = [];
      })
      .addCase(getInvestmentInsightsReport.pending, (state) => {
        state.listLoading.investmentInsights = true;
      })
      .addCase(getInvestmentInsightsReport.fulfilled, (state, action) => {
        state.listLoading.investmentInsights = false;
        state.investmentInsightsReport = action.payload.payload.users;
        state.totalUserHoldings = action.payload.payload.totalUserHoldings;
        state.listPagination.investmentInsights = readPagination(action.payload.payload);
      })
      .addCase(getInvestmentInsightsReport.rejected, (state) => {
        state.listLoading.investmentInsights = false;
        state.investmentInsightsReport = [];
        state.totalUserHoldings = null;
      })
      .addCase(getBalanceMismatchedUsers.pending, (state) => {
        state.listLoading.balanceMismatched = true;
      })
      .addCase(getBalanceMismatchedUsers.fulfilled, (state, action) => {
        state.listLoading.balanceMismatched = false;
        state.balanceMismatchedUsers = action.payload.payload.balanceMismatchedUsers;
        state.listPagination.balanceMismatched = readPagination(action.payload.payload);
      })
      .addCase(getBalanceMismatchedUsers.rejected, (state) => {
        state.listLoading.balanceMismatched = false;
        state.balanceMismatchedUsers = [];
        state.totalUserHoldings = null;
      })
      .addCase(pushCashback.pending, (state) => {
        state.isPushingCashback = true;
      })
      .addCase(pushCashback.fulfilled, (state) => {
        state.isPushingCashback = false;
      })
      .addCase(pushCashback.rejected, (state) => {
        state.isPushingCashback = false;
      })
      .addCase(getEligibleCashbackUser.pending, () => {
      })
      .addCase(getEligibleCashbackUser.fulfilled, (state, action) => {
        state.cashbackEligibleUsers = action.payload.payload.eligibleCashbackUsers;
        state.eligibleUsersPagination = {
          totalItems: action.payload.payload.totalItems,
          currentPage: action.payload.payload.page,
          totalPages: action.payload.payload.totalPages,
          hasNextPage: action.payload.payload.hasNextPage
        };
      })
      .addCase(getEligibleCashbackUser.rejected, (state) => {
        state.cashbackEligibleUsers = [];
        state.eligibleUsersPagination = initialPagination;
      })
      .addCase(getCashbackClients.pending, (state) => {
        state.isLoadingCashbackClients = true;
      })
      .addCase(getCashbackClients.fulfilled, (state, action) => {
        state.isLoadingCashbackClients = false;
        state.cashbackClients = action.payload.payload.eligibleCashbackUsers;
        state.cashbackClientsPagination = {
          totalItems: action.payload.payload.totalItems,
          currentPage: action.payload.payload.page,
          totalPages: action.payload.payload.totalPages,
          hasNextPage: action.payload.payload.hasNextPage
        };
      })
      // A date range with no statements answers 404, which lands here — an empty
      // list is the correct state for it.
      .addCase(getCashbackClients.rejected, (state) => {
        state.isLoadingCashbackClients = false;
        state.cashbackClients = [];
        state.cashbackClientsPagination = initialPagination;
      });
  }
});

export const { toggleAutoCashback } = reportsSlice.actions;

export default reportsSlice.reducer;